import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import profile01 from "@images/Profile_01.png";
import profile02 from "@images/Profile_02.png";
import profile03 from "@images/Profile_03.png";
import profile04 from "@images/Profile_04.png";

function useIntersection(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, visible };
}

const MEMBER_META = [
  {
    num: "01",
    photo: profile01,
    badge: "CEO",
    nameKey: "team.m1_name",
    titleKey: "team.m1_title",
    subtitleKey: "team.m1_subtitle",
    eduKey: "team.m1_edu",
    eduIcon: "ri-graduation-cap-line",
    bioKey: "team.m1_bio",
    tags: ["AI Strategy", "Data Science", "Product Vision", "Leadership"],
    reverse: false,
  },
  {
    num: "02",
    photo: profile02,
    badge: "CTO",
    nameKey: "team.m2_name",
    titleKey: "team.m2_title",
    subtitleKey: "team.m2_subtitle",
    eduKey: "team.m2_edu",
    eduIcon: "ri-graduation-cap-line",
    bioKey: "team.m2_bio",
    tags: ["AI Engineering", "Backend", "PostGIS", "ML/DL"],
    reverse: true,
  },
  {
    num: "03",
    photo: profile03,
    badge: "CMO",
    nameKey: "team.m3_name",
    titleKey: "team.m3_title",
    subtitleKey: "team.m3_subtitle",
    eduKey: "team.m3_edu",
    eduIcon: "ri-graduation-cap-line",
    bioKey: "team.m3_bio",
    tags: ["Brand Strategy", "Digital Marketing", "Growth Hacking", "K-Culture"],
    reverse: false,
  },
  {
    num: "04",
    photo: profile04,
    badge: "CLO",
    nameKey: "team.m4_name",
    titleKey: "team.m4_title",
    subtitleKey: "team.m4_subtitle",
    eduKey: "team.m4_edu",
    eduIcon: "ri-scales-3-line",
    bioKey: "team.m4_bio",
    tags: ["Legal Affairs", "Privacy Law", "AI Ethics", "Contract Law"],
    reverse: true,
  },
];

interface MemberCardProps {
  meta: typeof MEMBER_META[0];
  index: number;
}

function MemberCard({ meta, index }: MemberCardProps) {
  const { t } = useTranslation();
  const { ref, visible } = useIntersection(0.15);

  return (
    <div
      ref={ref}
      className={`py-14 md:py-20 border-b transition-all duration-700 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      }`}
      style={{
        borderColor: "rgba(255,255,255,0.06)",
        transitionDelay: `${index * 80}ms`,
      }}
    >
      <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${meta.reverse ? "lg:flex lg:flex-row-reverse" : ""}`}>
        {/* Photo */}
        <div className={`flex ${meta.reverse ? "lg:justify-end" : "lg:justify-start"} justify-center`}>
          <div className="relative">
            <div className="rounded-2xl overflow-hidden" style={{ width: "280px", height: "320px" }}>
              <img
                src={meta.photo}
                alt={`${t(meta.nameKey)} ${t(meta.titleKey)}`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div
              className={`absolute top-4 ${meta.reverse ? "left-4" : "right-4"} px-3 py-1.5 rounded-full font-label font-black text-sm whitespace-nowrap`}
              style={{
                backgroundColor: "oklch(0.74 0.18 55)",
                color: "oklch(0.10 0.001 10)",
              }}
            >
              {meta.badge}
            </div>
          </div>
        </div>

        {/* Info */}
        <div>
          <div className="font-heading font-black text-5xl mb-4" style={{ color: "rgba(255,255,255,0.08)" }}>
            {meta.num}
          </div>
          <h3 className="font-heading font-black text-3xl md:text-4xl mb-1" style={{ color: "white" }}>
            {t(meta.nameKey)}
          </h3>
          <p className="font-label font-semibold text-base mb-1" style={{ color: "oklch(0.74 0.18 55)" }}>
            {t(meta.titleKey)}
          </p>
          <p className="font-label text-sm mb-4" style={{ color: "rgba(255,255,255,0.35)" }}>
            {t(meta.subtitleKey)}
          </p>

          {meta.eduKey && meta.eduIcon && (
            <div className="flex items-center gap-2 mb-5">
              <i className={`${meta.eduIcon} text-sm`} style={{ color: "oklch(0.74 0.18 55)" }}></i>
              <span className="text-sm font-label" style={{ color: "rgba(255,255,255,0.5)" }}>
                {t(meta.eduKey)}
              </span>
            </div>
          )}

          <div className="h-px mb-5" style={{ backgroundColor: "rgba(255,255,255,0.06)" }}></div>

          <p className="text-sm font-label leading-relaxed mb-6" style={{ color: "rgba(255,255,255,0.55)" }}>
            {t(meta.bioKey)}
          </p>

          <div className="flex flex-wrap gap-2">
            {meta.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 rounded-full text-xs font-label whitespace-nowrap"
                style={{
                  backgroundColor: "rgba(245,158,11,0.10)",
                  color: "oklch(0.74 0.18 55)",
                  border: "1px solid rgba(245,158,11,0.20)",
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TeamSection() {
  const { t } = useTranslation();
  const { ref, visible } = useIntersection();

  return (
    <section
      id="team"
      className="py-24 md:py-28 px-4"
      style={{ backgroundColor: "oklch(0.13 0.001 10)" }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div
          ref={ref}
          className={`text-center mb-4 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <p className="text-xs font-label tracking-widest uppercase mb-4" style={{ color: "oklch(0.74 0.18 55)" }}>
            {t("team.label")}
          </p>
          <h2 className="font-heading font-black text-3xl md:text-5xl mb-4 leading-tight" style={{ color: "white" }}>
            {t("team.title1")}<br />{t("team.title2")}
          </h2>
          <p className="font-label text-sm md:text-base" style={{ color: "rgba(255,255,255,0.45)" }}>
            {t("team.desc")}
          </p>
        </div>

        {MEMBER_META.map((meta, i) => (
          <MemberCard key={meta.num} meta={meta} index={i} />
        ))}
      </div>
    </section>
  );
}